import { cn } from '@/lib/utils'
import { CARD_PAGE_PX_CLASS } from '@/components/catalog/cardGrid'
import { PAGE_MIN_H_CLASS } from '@/components/layout/pageHeight'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  useGetMyUnpublishedCoursesQuery,
  useGetMyPublishedCoursesQuery,
  useDeleteCourseMutation,
} from '@/features/editor/coursesApi'
import { useCreateCourse } from '@/features/editor/hooks/useCreateCourse'
import { DashboardCourseGrid } from '@/features/catalog/components/dashboard/DashboardCourseGrid'
import {
  matchesDashboardFilter,
  matchesDashboardSearch,
} from '@/features/catalog/components/dashboard/dashboardFilter'
import {
  DashboardHeader,
  type DashboardFilter,
} from '@/features/catalog/components/dashboard/DashboardHeader'
import { DeleteCourseDialog } from '@/features/catalog/components/dashboard/DeleteCourseDialog'
import type { Course } from '@/types/course'
import { useMessages } from '@/i18n'
import { messages } from './messages'

const Dashboard = () => {
  const { data: drafts = [], isLoading: draftsLoading } = useGetMyUnpublishedCoursesQuery()
  const { data: published = [], isLoading: publishedLoading } = useGetMyPublishedCoursesQuery()
  const { handleCreate, isCreating } = useCreateCourse()
  const [deleteCourse] = useDeleteCourseMutation()
  const [filter, setFilter] = useState<DashboardFilter>('all')
  const [search, setSearch] = useState('')
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null)
  const m = useMessages(messages)

  // Сверху самые свежие: по номеру курса, от большего к меньшему.
  const data = [...drafts, ...published].sort((a, b) => b.id - a.id)
  const isLoading = draftsLoading || publishedLoading

  const handleDelete = async () => {
    if (!courseToDelete) return
    try {
      await deleteCourse(courseToDelete.id).unwrap()
      toast.success(m.courseDeleted)
    } catch {
      toast.error(m.courseDeleteFailed)
    }
    setCourseToDelete(null)
  }

  // Счётчики — по найденному поиском (иначе «Все 14» над четырьмя карточками),
  // и только когда приехали ОБА списка: пока грузится один, числа врали
  // («Все 7 / Черновики 7 / 0 / 0»).
  const searched = data.filter((c) => matchesDashboardSearch(c, search))
  const counts: Record<DashboardFilter, number> | null = isLoading
    ? null
    : {
        all: searched.length,
        drafts: searched.filter((c) => matchesDashboardFilter(c, 'drafts')).length,
        published: searched.filter((c) => matchesDashboardFilter(c, 'published')).length,
        link: searched.filter((c) => matchesDashboardFilter(c, 'link')).length,
      }

  return (
    <div className={cn(PAGE_MIN_H_CLASS, 'relative overflow-auto')}>
      <div
        className={cn('relative z-10 py-4 md:py-6 transition-all duration-200', CARD_PAGE_PX_CLASS)}
      >
        <div className="h-2" />

        <DashboardHeader
          filter={filter}
          counts={counts}
          search={search}
          hasCourses={data.length > 0}
          onFilterChange={setFilter}
          onSearchChange={setSearch}
        />

        <DashboardCourseGrid
          courses={data}
          isLoading={isLoading}
          filter={filter}
          search={search}
          onRequestDelete={setCourseToDelete}
          onCreate={handleCreate}
          isCreating={isCreating}
        />
      </div>

      <DeleteCourseDialog
        course={courseToDelete}
        onCancel={() => setCourseToDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  )
}

export default Dashboard
